import { useQueryClient } from '@tanstack/react-query';

import {
  deleteCommunityPostSubComment,
  patchCommunityPostSubComment,
  postCommunityPostSubComment,
} from '@/api/community/subComment';

import { useAuthMutation } from '../common/auth';
import { communityPostCommentQueryKey } from './comment';
import { communityPostQueryKey } from './post';

const communityPostSubCommentQueryKey = {
  all: ['community-post-subComment'] as const,
  post: (commentId: string) =>
    [...communityPostSubCommentQueryKey.all, 'post', commentId] as const,
  patch: (subCommentId: string) =>
    [...communityPostSubCommentQueryKey.all, 'patch', subCommentId] as const,
  delete: (subCommentId: string) =>
    [...communityPostSubCommentQueryKey.all, 'delete', subCommentId] as const,
};

export function usePostCommunityPostSubComment({
  postId,
  commentId,
}: {
  postId: string;
  commentId: string;
}) {
  const queryClient = useQueryClient();

  return useAuthMutation({
    mutationKey: communityPostSubCommentQueryKey.post(commentId),
    mutationFn: postCommunityPostSubComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: communityPostQueryKey.detail(postId),
      });
      await queryClient.invalidateQueries({
        queryKey: communityPostCommentQueryKey.comments({
          postId,
          sortBy: 'liked',
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: communityPostCommentQueryKey.comments({
          postId,
          sortBy: 'created',
        }),
      });
    },
  });
}

export function usePatchCommunityPostSubComment({
  postId,
  subCommentId,
}: {
  postId: string;
  subCommentId: string;
}) {
  const queryClient = useQueryClient();

  return useAuthMutation({
    mutationKey: communityPostSubCommentQueryKey.patch(subCommentId),
    mutationFn: patchCommunityPostSubComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communityPostQueryKey.detail(postId),
      });
      queryClient.invalidateQueries({
        queryKey: communityPostCommentQueryKey.comments({
          postId,
          sortBy: 'liked',
        }),
      });
      queryClient.invalidateQueries({
        queryKey: communityPostCommentQueryKey.comments({
          postId,
          sortBy: 'created',
        }),
      });
    },
  });
}

export function useDeleteCommunityPostSubComment({
  postId,
  subCommentId,
}: {
  postId: string;
  subCommentId: string;
}) {
  const queryClient = useQueryClient();

  return useAuthMutation({
    mutationKey: communityPostSubCommentQueryKey.delete(subCommentId),
    mutationFn: deleteCommunityPostSubComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communityPostQueryKey.detail(postId),
      });
      queryClient.invalidateQueries({
        queryKey: communityPostCommentQueryKey.comments({
          postId,
          sortBy: 'liked',
        }),
      });
      queryClient.invalidateQueries({
        queryKey: communityPostCommentQueryKey.comments({
          postId,
          sortBy: 'created',
        }),
      });
    },
  });
}
